/**
 * Client-side photo quality probe.
 *
 * Runs 3 lightweight heuristics in a hidden canvas BEFORE the file is
 * uploaded to the AI endpoints. Zero LLM cost, ~50ms on a mid-range phone.
 *
 * Warnings (not blocking): we tell the user but let them upload anyway —
 * the AI is always better than nothing and some scenes look worse on probes
 * than they really are.
 *
 *   - resolution: < 900px on the long side → "bassa risoluzione"
 *   - brightness: average luma < 35 or > 225 → "scura/bruciata"
 *   - sharpness: Laplacian-like variance < 60 → "mossa/sfocata"
 */

export async function probePhotoQuality(file) {
  if (!file || !(file instanceof Blob)) {
    return { ok: true, warnings: [] };
  }

  try {
    const url = URL.createObjectURL(file);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);

    const warnings = [];

    // 1) Resolution
    const longSide = Math.max(img.naturalWidth, img.naturalHeight);
    if (longSide < 900) {
      warnings.push({
        code: "low_resolution",
        message: `Foto a bassa risoluzione (${img.naturalWidth}×${img.naturalHeight}px). Per il massimo effetto scatta da più vicino o usa la fotocamera posteriore.`,
      });
    }

    // Downscale to a small canvas (max 360px) for brightness + sharpness
    // — same probe either way, much faster.
    const MAX = 360;
    const scale = Math.min(1, MAX / longSide);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    // 2) Brightness (rec.601 luma, mean over pixels)
    let sum = 0;
    const n = w * h;
    const gray = new Uint8ClampedArray(n);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      gray[p] = y;
      sum += y;
    }
    const avgLuma = sum / n;

    if (avgLuma < 35) {
      warnings.push({
        code: "too_dark",
        message: "La foto è molto scura. Accendi le luci o apri le tende — sappiamo fare miracoli, ma non possiamo inventare i dettagli che non ci sono.",
      });
    } else if (avgLuma > 225) {
      warnings.push({
        code: "blown_out",
        message: "La foto è sovraesposta (bruciata). Tocca lo schermo su una zona chiara prima di scattare, così la fotocamera abbassa l'esposizione.",
      });
    }

    // 3) Sharpness: Laplacian-like edge variance
    // kernel: [0,1,0; 1,-4,1; 0,1,0]. Higher variance = more edges = sharp.
    let edgeSum = 0;
    let edgeSq = 0;
    let count = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const lap =
          -4 * gray[i] +
          gray[i - 1] +
          gray[i + 1] +
          gray[i - w] +
          gray[i + w];
        edgeSum += lap;
        edgeSq += lap * lap;
        count++;
      }
    }
    const mean = edgeSum / count;
    const variance = edgeSq / count - mean * mean;

    if (variance < 60) {
      warnings.push({
        code: "blurry",
        message: "La foto sembra mossa o sfocata. Appoggia il telefono a un muro o usa il timer di 3 secondi per evitare vibrazioni.",
      });
    }

    return {
      ok: true,
      resolution: { w: img.naturalWidth, h: img.naturalHeight },
      brightness: avgLuma,
      sharpness: variance,
      warnings,
    };
  } catch (e) {
    // Don't block the upload on probe failures — fall through silently.
    return { ok: false, error: String(e), warnings: [] };
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
